import {
    fetchDnaAncestorPainting,
    fetchDnaReconstructedAncestors,
    fetchDnaSegmentAncestryOverlay,
    fetchDnaSegmentEntries,
    fetchDnaSegmentsByEntry,
} from '@/api/apiService';

export function useDnaSegments() {
    // Caches are scoped to this composable instance to avoid refetching unchanged
    // DNA data while users move between entries in the same view.
    const entriesCache = new Map();
    const segmentsCache = new Map();
    const ancestryOverlayCache = new Map();
    let ancestorPaintingCache = null;
    let reconstructedAncestorsCache = null;

    const buildEntryId = (testerCategory, personID) => `${testerCategory}:${personID}`;

    const loadSegmentEntries = async () => {
        const response = await fetchDnaSegmentEntries();
        const entries = Array.isArray(response?.entries) ? response.entries : [];

        // Rebuild the entry index each time to keep metadata aligned with backend snapshots.
        entriesCache.clear();
        entries.forEach((entry) => {
            if (entry?.id) {
                entriesCache.set(entry.id, entry);
            }
        });

        return {
            entries,
            meta: {
                generatedAt: response?.generatedAt || null,
                totalCsvFiles: Number(response?.totalCsvFiles) || 0,
                totalResolvedEntries: Number(response?.totalResolvedEntries) || entries.length,
                unresolvedFiles: Array.isArray(response?.unresolvedFiles) ? response.unresolvedFiles : [],
            },
        };
    };

    const loadSegmentsForEntry = async (entry) => {
        if (!entry?.entryKey && (!entry?.testerCategory || !entry?.personID)) {
            throw new Error('Missing DNA segment entry key.');
        }

        const entryId = entry.id || entry.entryKey || buildEntryId(entry.testerCategory, entry.personID);
        if (segmentsCache.has(entryId)) {
            // Segment lists are immutable for a given generated dataset, so a cache hit
            // can be returned directly.
            return segmentsCache.get(entryId);
        }

        const response = await fetchDnaSegmentsByEntry(entry);
        const segments = Array.isArray(response?.segments) ? response.segments : [];

        if (!entriesCache.has(entryId)) {
            // Keep the entry index complete even when callers open an entry directly.
            entriesCache.set(entryId, {
                id: entryId,
                testerCategory: entry.testerCategory,
                personID: entry.personID,
                matchName: response?.entry?.matchName || entry.matchName || '',
                fileName: response?.entry?.fileName || entry.fileName || '',
            });
        }

        segmentsCache.set(entryId, segments);
        return segments;
    };

    const loadAncestryOverlayForEntry = async (entry) => {
        if (!entry?.entryKey && (!entry?.testerCategory || !entry?.personID)) {
            throw new Error('Missing DNA ancestry overlay entry key.');
        }

        const entryId = entry.id || buildEntryId(entry.testerCategory, entry.personID);
        if (ancestryOverlayCache.has(entryId)) {
            return ancestryOverlayCache.get(entryId);
        }

        const response = await fetchDnaSegmentAncestryOverlay(entry);
        // Provide a stable empty shape so consuming components can render without
        // additional null checks when no overlay is available.
        const overlay = response?.overlay || {
            available: false,
            testerCategory: entry.testerCategory,
            slicesBySegmentKey: {},
            legend: [],
        };

        ancestryOverlayCache.set(entryId, overlay);
        return overlay;
    };

    const summarizeSegments = (segments) => {
        // Aggregate metrics used by overview cards and quick statistics.
        const totalCentimorgans = segments.reduce((sum, segment) => sum + (segment.centimorgans || 0), 0);
        const totalSnps = segments.reduce((sum, segment) => sum + (segment.snps || 0), 0);
        const chromosomeCount = new Set(segments.map(segment => segment.chromosome)).size;

        return {
            totalCentimorgans,
            totalSnps,
            chromosomeCount,
        };
    };

    const loadAncestorPainting = async () => {
        if (ancestorPaintingCache) {
            return ancestorPaintingCache;
        }

        const response = await fetchDnaAncestorPainting();
        ancestorPaintingCache = {
            testers: Array.isArray(response?.testers) ? response.testers : [],
            assignments: Array.isArray(response?.assignments) ? response.assignments : [],
            summaryByAncestor: Array.isArray(response?.summaryByAncestor) ? response.summaryByAncestor : [],
            summaryByTester: Array.isArray(response?.summaryByTester) ? response.summaryByTester : [],
            unresolved: Array.isArray(response?.unresolved) ? response.unresolved : [],
            totals: response?.totals || {
                assignedCentimorgans: 0,
                autosomalCmTotalEstimate: 6800,
                assignmentCount: 0,
                unresolvedCount: 0,
            },
            generatedAt: response?.generatedAt || null,
            sourceGeneratedAt: response?.sourceGeneratedAt || null,
        };
        return ancestorPaintingCache;
    };

    const loadReconstructedAncestors = async () => {
        if (reconstructedAncestorsCache) {
            return reconstructedAncestorsCache;
        }

        const response = await fetchDnaReconstructedAncestors();
        reconstructedAncestorsCache = {
            ancestors: Array.isArray(response?.ancestors) ? response.ancestors : [],
            unresolved: Array.isArray(response?.unresolved) ? response.unresolved : [],
            totals: response?.totals || {
                reconstructedAncestorCount: 0,
                assignedCentimorgans: 0,
                autosomalCmTotalEstimate: 6800,
                includedSegmentCount: 0,
                excludedWeakSegmentCount: 0,
                unresolvedRelationshipCount: 0,
            },
            thresholds: response?.thresholds || {
                minCentimorgans: 0,
                minSnps: 0,
            },
            generatedAt: response?.generatedAt || null,
            sourceGeneratedAt: response?.sourceGeneratedAt || null,
        };
        return reconstructedAncestorsCache;
    };

    return {
        buildEntryId,
        loadSegmentEntries,
        loadSegmentsForEntry,
        loadAncestryOverlayForEntry,
        loadAncestorPainting,
        loadReconstructedAncestors,
        summarizeSegments,
    };
}
